import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconDot } from "@/assets/icons/geist/IconDot";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import {
    useCreateEnvironment,
    useListEnvironments,
    useStartEnvironment,
    useUpdateEnvironment,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { getEnvironmentDisplayName } from "@/utils/environment";
import { formatError } from "@/utils/errors";
import { EnvironmentPhase, KernelControlsConfig, Veto, Veto_Exec } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { sortEnvironmentClassEntries, useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";

type PreviewInEnvironmentButtonProps = {
    executables: string[];
    enabled: boolean;
    disabled?: boolean;
};

type PreviewState = "idle" | "applying" | "applied" | "drifted";

function buildKernelControlsConfig(executables: string[], enabled: boolean): KernelControlsConfig {
    return new KernelControlsConfig({
        veto: new Veto({
            exec: new Veto_Exec({
                enabled,
                denylist: executables,
            }),
        }),
    });
}

function arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
}

function isEnvironmentRunning(env: PlainEnvironment): boolean {
    return env.status?.phase === EnvironmentPhase.RUNNING;
}

function isEnvironmentStopped(env: PlainEnvironment): boolean {
    return env.status?.phase === EnvironmentPhase.STOPPED;
}

function isEnvironmentTransitioning(env: PlainEnvironment): boolean {
    const phase = env.status?.phase;
    return (
        phase === EnvironmentPhase.STARTING ||
        phase === EnvironmentPhase.CREATING ||
        phase === EnvironmentPhase.UPDATING
    );
}

function envDotColor(env: PlainEnvironment | undefined): string {
    if (!env) return "text-content-tertiary";
    if (isEnvironmentRunning(env)) return "text-content-green";
    if (isEnvironmentTransitioning(env)) return "text-content-orange";
    return "text-content-tertiary";
}

function envStatusLabel(env: PlainEnvironment | undefined): string {
    if (!env) return "No environment selected";
    if (isEnvironmentRunning(env)) return "Environment running";
    if (isEnvironmentTransitioning(env)) return "Environment starting";
    if (isEnvironmentStopped(env)) return "Environment stopped";
    return "Unknown";
}

function previewStateTooltip(state: PreviewState): string {
    switch (state) {
        case "idle":
            return "Test your deny list config against a running environment before saving";
        case "applying":
            return "Applying config\u2026";
        case "applied":
            return "Config applied \u2014 click to open environment";
        case "drifted":
            return "Config changed since last preview \u2014 click to re-apply";
    }
}

/**
 * Preview state indicator shown to the left of the button label.
 * Communicates whether the deny list config has been pushed to the environment.
 */
const PreviewStateIcon: FC<{ state: PreviewState }> = ({ state }) => {
    switch (state) {
        case "applying":
            return (
                <IconSpinner
                    size="sm"
                    className="animate-spin text-content-secondary"
                    aria-label="Applying config"
                    data-testid="preview-state-applying"
                />
            );
        case "applied":
            return (
                <IconCheck
                    size="sm"
                    className="text-content-green"
                    aria-label="Config applied"
                    data-testid="preview-state-applied"
                />
            );
        case "drifted":
            return (
                <IconWarningCircle
                    size="sm"
                    className="text-content-orange"
                    aria-label="Config out of sync"
                    data-testid="preview-state-drifted"
                />
            );
        default:
            return null;
    }
};

/**
 * Environment status dot shown to the right of the button label.
 * Communicates whether the selected environment is running/starting/stopped,
 * independent of the preview (config apply) state.
 */
const EnvStatusIndicator: FC<{ env: PlainEnvironment | undefined }> = ({ env }) => {
    if (!env) return null;

    const label = envStatusLabel(env);

    return (
        <Tooltip content={label} usePortal>
            <IconDot
                size="sm"
                className={cn("shrink-0", envDotColor(env))}
                aria-label={label}
                data-testid="preview-env-status-dot"
            />
        </Tooltip>
    );
};

export const PreviewInEnvironmentButton: FC<PreviewInEnvironmentButtonProps> = ({
    executables,
    enabled,
    disabled = false,
}) => {
    const { toast } = useToast();
    const updateEnvironment = useUpdateEnvironment();
    const startEnvironment = useStartEnvironment();
    const createEnvironment = useCreateEnvironment();
    const { data: environmentsData } = useListEnvironments();
    const { environmentClassEntries, isLoading: isLoadingClasses } = useEnvironmentClassEntries();

    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string | null>(null);
    const [lastAppliedExecutables, setLastAppliedExecutables] = useState<string[] | null>(null);
    const [lastAppliedEnabled, setLastAppliedEnabled] = useState<boolean | null>(null);
    const [isApplying, setIsApplying] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const pendingApplyRef = useRef(false);

    const environments = useMemo(() => environmentsData?.environments ?? [], [environmentsData]);

    const selectedEnvironment = useMemo(
        () => environments.find((e) => e.id === selectedEnvironmentId),
        [environments, selectedEnvironmentId],
    );

    // Preview state: tracks whether the in-memory config has been pushed to the environment
    const previewState: PreviewState = useMemo(() => {
        if (isApplying || isCreating) return "applying";
        if (lastAppliedExecutables === null) return "idle";
        if (!arraysEqual(executables, lastAppliedExecutables) || enabled !== lastAppliedEnabled) return "drifted";
        return "applied";
    }, [isApplying, isCreating, lastAppliedExecutables, lastAppliedEnabled, executables, enabled]);

    const { runningEnvironments, stoppedEnvironments } = useMemo(() => {
        const running: PlainEnvironment[] = [];
        const stopped: PlainEnvironment[] = [];
        for (const env of environments) {
            if (isEnvironmentRunning(env) || isEnvironmentTransitioning(env)) running.push(env);
            else if (isEnvironmentStopped(env)) stopped.push(env);
        }
        const byName = (a: PlainEnvironment, b: PlainEnvironment) =>
            getEnvironmentDisplayName(a).localeCompare(getEnvironmentDisplayName(b));
        running.sort(byName);
        stopped.sort(byName);
        return { runningEnvironments: running, stoppedEnvironments: stopped };
    }, [environments]);

    const applyConfig = useCallback(
        async (environmentId: string) => {
            setIsApplying(true);
            try {
                await updateEnvironment.mutateAsync({
                    req: {
                        environmentId,
                        spec: {
                            kernelControlsConfig: buildKernelControlsConfig(executables, enabled),
                        },
                    },
                });
                setLastAppliedExecutables([...executables]);
                setLastAppliedEnabled(enabled);
                toast({ title: "Deny list config applied to environment" });
            } catch (error) {
                console.error(error);
                toast({
                    title: "Failed to apply config to environment",
                    description: formatError(error),
                });
            } finally {
                setIsApplying(false);
            }
        },
        [executables, enabled, updateEnvironment, toast],
    );

    // Auto-apply when a pending environment reaches RUNNING
    useEffect(() => {
        if (pendingApplyRef.current && selectedEnvironment && isEnvironmentRunning(selectedEnvironment)) {
            pendingApplyRef.current = false;
            void applyConfig(selectedEnvironment.id);
        }
    }, [selectedEnvironment, applyConfig]);

    const handleSelectEnvironment = useCallback(
        async (env: PlainEnvironment) => {
            setSelectedEnvironmentId(env.id);
            setLastAppliedExecutables(null);
            setLastAppliedEnabled(null);

            if (isEnvironmentRunning(env)) {
                await applyConfig(env.id);
            } else if (isEnvironmentStopped(env)) {
                pendingApplyRef.current = true;
                try {
                    await startEnvironment.mutateAsync({ environmentId: env.id });
                } catch (error) {
                    pendingApplyRef.current = false;
                    console.error(error);
                    toast({
                        title: "Failed to start environment",
                        description: formatError(error),
                    });
                }
            }
        },
        [applyConfig, startEnvironment, toast],
    );

    const handleCreateNewEnvironment = useCallback(async () => {
        const sorted = sortEnvironmentClassEntries(environmentClassEntries);
        const defaultClass = sorted[0];
        if (!defaultClass) {
            toast({ title: "No environment class available" });
            return;
        }

        setIsCreating(true);
        pendingApplyRef.current = true;
        try {
            const env = await createEnvironment.mutateAsync({
                type: "blank",
                classID: defaultClass.clazz.id,
            });
            setSelectedEnvironmentId(env.id);
            setLastAppliedExecutables(null);
            setLastAppliedEnabled(null);
        } catch (error) {
            pendingApplyRef.current = false;
            console.error(error);
            toast({
                title: "Failed to create environment",
                description: formatError(error),
            });
        } finally {
            setIsCreating(false);
        }
    }, [environmentClassEntries, createEnvironment, toast]);

    const handlePrimaryClick = useCallback(() => {
        if (!selectedEnvironmentId) return;

        if (previewState === "applied") {
            window.open(getDetailsURL({ environment: { id: selectedEnvironmentId } }), "_blank");
        } else if (previewState === "drifted" || previewState === "idle") {
            void applyConfig(selectedEnvironmentId);
        }
    }, [selectedEnvironmentId, previewState, applyConfig]);

    const selectedName = selectedEnvironment ? getEnvironmentDisplayName(selectedEnvironment) : null;

    const primaryButtonDisabled = disabled || !selectedEnvironmentId || previewState === "applying";

    return (
        <div className="flex flex-col gap-1.5" data-testid="preview-in-environment">
            <Tooltip content={previewStateTooltip(previewState)} usePortal>
                <ButtonGroup variant="secondary">
                    <ButtonGroupItem
                        onClick={handlePrimaryClick}
                        disabled={primaryButtonDisabled}
                        data-testid="preview-in-environment-button"
                        data-tracking-id="preview-in-environment"
                    >
                        <span className="flex items-center gap-1.5">
                            <PreviewStateIcon state={previewState} />
                            <span>{selectedName ? `Preview in ${selectedName}` : "Preview in\u2026"}</span>
                            <EnvStatusIndicator env={selectedEnvironment} />
                        </span>
                    </ButtonGroupItem>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ButtonGroupItem
                                data-testid="preview-in-environment-dropdown"
                                LeadingIcon={IconChevronDownSmall}
                                aria-label="Select environment for preview"
                                disabled={disabled}
                            />
                        </DropdownMenuTrigger>
                        <DropdownMenuPortal>
                            <DropdownMenuContent
                                align="end"
                                className="w-auto min-w-64 max-h-80 overflow-y-auto"
                            >
                                {runningEnvironments.length > 0 && (
                                    <>
                                        <DropdownMenuLabel>Running</DropdownMenuLabel>
                                        {runningEnvironments.map((env) => (
                                            <EnvironmentDropdownItem
                                                key={env.id}
                                                env={env}
                                                selected={env.id === selectedEnvironmentId}
                                                onClick={() => handleSelectEnvironment(env)}
                                                data-tracking-id="preview-select-running-env"
                                            />
                                        ))}
                                    </>
                                )}
                                {stoppedEnvironments.length > 0 && (
                                    <>
                                        {runningEnvironments.length > 0 && <DropdownMenuSeparator />}
                                        <DropdownMenuLabel>Stopped</DropdownMenuLabel>
                                        {stoppedEnvironments.map((env) => (
                                            <EnvironmentDropdownItem
                                                key={env.id}
                                                env={env}
                                                selected={env.id === selectedEnvironmentId}
                                                onClick={() => handleSelectEnvironment(env)}
                                                data-tracking-id="preview-select-stopped-env"
                                            />
                                        ))}
                                    </>
                                )}
                                {environments.length === 0 && (
                                    <div className="px-3 py-2">
                                        <Text className="text-sm text-content-secondary">No environments found</Text>
                                    </div>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={handleCreateNewEnvironment}
                                    disabled={isLoadingClasses || environmentClassEntries.length === 0}
                                    data-testid="preview-create-new-environment"
                                    data-tracking-id="preview-create-new-environment"
                                >
                                    <IconPlus size="sm" className="mr-2" aria-hidden />
                                    <span>Create new environment</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenuPortal>
                    </DropdownMenu>
                </ButtonGroup>
            </Tooltip>
            {previewState === "drifted" && (
                <Text
                    className="text-xs text-content-orange"
                    data-testid="preview-config-drift-warning"
                >
                    Config changed since last preview
                </Text>
            )}
        </div>
    );
};

const EnvironmentDropdownItem: FC<{
    env: PlainEnvironment;
    selected: boolean;
    onClick: () => void;
    "data-tracking-id": string;
}> = ({ env, selected, onClick, "data-tracking-id": trackingId }) => {
    const name = getEnvironmentDisplayName(env);

    return (
        <DropdownMenuItem
            onClick={onClick}
            className={cn(selected && "bg-surface-secondary")}
            aria-current={selected ? "true" : undefined}
            data-testid={`preview-env-item-${env.id}`}
            data-tracking-id={trackingId}
        >
            <span className="flex items-center gap-2 flex-1 min-w-0">
                <EnvironmentStatusDot env={env} size="sm" />
                <span className="truncate max-w-48">{name}</span>
            </span>
            {selected && (
                <IconCheck size="sm" className="shrink-0 text-content-green ml-2" aria-hidden />
            )}
        </DropdownMenuItem>
    );
};
